import React, { useState, useImperativeHandle, forwardRef, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, Modal, Platform, useWindowDimensions, ScrollView } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { FontAwesome6 } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { YStack, XStack } from 'tamagui';

export interface OTPModalRef {
  show: (userData: any) => void;
  hide: () => void;
}

interface OTPModalProps {
  onVerificationComplete: (userData: any, otpCode: string) => Promise<{ success: boolean }> | { success: boolean };
  onCancel?: () => void;
  theme?: boolean;
}

const OTPModal = forwardRef<OTPModalRef, OTPModalProps>(({ 
  onVerificationComplete, 
  onCancel,
  theme = true 
}, ref) => {
  const [visible, setVisible] = useState(false);
  const [userData, setUserData] = useState(null);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const windowDimensions = useWindowDimensions();
  const inputRefs = useRef<TextInput[]>([]);

  // Modern responsive breakpoints
  const getResponsiveBreakpoints = () => {
    const { width, height } = windowDimensions;
    return {
      isMobile: width < 768,
      isTablet: width >= 768 && width < 1024,
      isDesktop: width >= 1024,
      isShortScreen: height < 600,
      isMediumScreen: height >= 600 && height < 900,
    };
  };

  const breakpoints = getResponsiveBreakpoints();

  // Modern responsive scaling system
  const getResponsiveStyles = () => {
    const { isMobile, isTablet, isDesktop, isShortScreen, isMediumScreen } = breakpoints;
    
    const getScaleFactor = () => {
      if (isMobile && isShortScreen) return 0.75;
      if (isMobile) return 0.85;
      if (isTablet) return 0.95;
      return 1;
    };

    const scaleFactor = getScaleFactor();

    return {
      spacing: {
        xs: Math.max(4, 8 * scaleFactor),
        sm: Math.max(8, 12 * scaleFactor),
        md: Math.max(12, 16 * scaleFactor),
        lg: Math.max(16, 24 * scaleFactor),
        xl: Math.max(24, 32 * scaleFactor),
        xxl: Math.max(32, 48 * scaleFactor),
      },
      typography: {
        xs: Math.max(10, 12 * scaleFactor),
        sm: Math.max(12, 14 * scaleFactor),
        base: Math.max(14, 16 * scaleFactor),
        lg: Math.max(16, 18 * scaleFactor),
        xl: Math.max(18, 20 * scaleFactor),
        xxl: Math.max(20, 24 * scaleFactor),
      },
      components: {
        cardRadius: isMobile ? 12 : 16,
        buttonHeight: Math.max(44, 48 * scaleFactor),
        inputHeight: Math.max(40, 44 * scaleFactor),
        iconSize: Math.max(20, 24 * scaleFactor),
      }
    };
  };

  const responsive = getResponsiveStyles();

  useImperativeHandle(ref, () => ({
    show: (newUserData: any) => {
      setUserData(newUserData);
      setVisible(true);
      setOtpCode(['', '', '', '', '', '']);
      setError('');
      setIsVerifying(false);
    },
    hide: () => {
      setVisible(false);
      setOtpCode(['', '', '', '', '', '']);
      setError('');
      setIsVerifying(false);
    }
  }));

  const handleOtpChange = (value: string, index: number) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6);
      const newOtpCode = [...otpCode];
      for (let i = 0; i < pastedCode.length; i++) {
        if (index + i < 6) {
          newOtpCode[index + i] = pastedCode[i];
        }
      }
      setOtpCode(newOtpCode);
      
      // Focus next empty input or last input
      const nextIndex = Math.min(index + pastedCode.length, 5);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
    } else {
      // Handle single character
      const newOtpCode = [...otpCode];
      newOtpCode[index] = value;
      setOtpCode(newOtpCode);

      // Auto-focus next input
      if (value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }
    setError('');
  };

  const handleKeyPress = (key: string, index: number) => {
    if (key === 'Backspace' && !otpCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    const code = otpCode.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      // Simulate API call for OTP verification
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In real implementation, call your OTP verification API here
      console.log('🔐 Verifying OTP:', code, 'for user:', userData?.email);
      
      // For demo purposes, accept any 6-digit code
      const res = await onVerificationComplete(userData, code);
      if (res?.success) {
        console.log('✅ OTP verification successful');
        setVisible(false);
      } else {
        console.log('❌ OTP verification failed');
      }
    } catch (error) {
      setError('Invalid verification code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setResendCooldown(30);
    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    try {
      // Simulate resend API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('📨 Resending OTP to:', userData?.email);
    } catch (error) {
      setError('Failed to resend code. Please try again.');
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setOtpCode(['', '', '', '', '', '']);
    setError('');
    setIsVerifying(false);
    onCancel?.();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
    >
      <View style={{
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: responsive.spacing.lg,
        paddingHorizontal: Math.max(responsive.spacing.lg, 20),
      }}>
        <Animatable.View
          animation="zoomIn"
          duration={400}
          style={{
            width: '100%',
            maxWidth: breakpoints.isMobile ? Math.min(350, windowDimensions.width - 40) : 450,
            minWidth: 280,
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.98)' : 'rgba(30, 30, 30, 0.98)',
            borderRadius: responsive.components.cardRadius + 4,
            padding: responsive.spacing.xl,
            paddingHorizontal: Math.max(responsive.spacing.lg, 16),
            borderWidth: 1,
            borderColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
            maxHeight: windowDimensions.height - 100,
            ...Platform.select({
              web: {
                backdropFilter: 'blur(20px)',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              },
              default: {
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 20 },
                shadowOpacity: 0.3,
                shadowRadius: 20,
                elevation: 25,
              }
            }),
          }}
        >
          <ScrollView 
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
          {/* Header */}
          <YStack alignItems="center" space="$3" marginBottom="$6">
            <View style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: '#3B82F6',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: responsive.spacing.sm,
            }}>
              <FontAwesome6
                name="shield-halved"
                size={36}
                color="white"
              />
            </View>
            
            <Text style={{
              fontSize: responsive.typography.xl,
              fontWeight: '700',
              color: theme ? '#1F2937' : '#F9FAFB',
              textAlign: 'center',
              marginBottom: responsive.spacing.xs,
            }}>
              Verify Your Email
            </Text>
            
            <Text style={{
              fontSize: responsive.typography.base,
              color: theme ? '#6B7280' : '#D1D5DB',
              textAlign: 'center',
              lineHeight: responsive.typography.base * 1.4,
            }}>
              We've sent a 6-digit verification code to{'\n'}
              <Text style={{ fontWeight: '600', color: '#3B82F6' }}>
                {userData?.email || 'your email'}
              </Text>
            </Text>
          </YStack>

          {/* OTP Input */}
          <YStack space="$4" marginBottom="$6">
            <View style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingHorizontal: responsive.spacing.xs,
            }}>
              {otpCode.map((digit, index) => (
                <TextInput
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  style={{
                    width: breakpoints.isMobile ? 40 : 50,
                    height: responsive.components.inputHeight + 10,
                    minWidth: 35,
                    maxWidth: 55,
                    borderWidth: 2,
                    borderColor: digit ? '#3B82F6' : (theme ? '#E5E7EB' : '#4B5563'),
                    borderRadius: 12,
                    textAlign: 'center',
                    fontSize: responsive.typography.lg,
                    fontWeight: '600',
                    color: theme ? '#1F2937' : '#F9FAFB',
                    backgroundColor: theme ? '#F9FAFB' : '#374151',
                    marginHorizontal: 2,
                    ...Platform.select({
                      web: {
                        outline: 'none',
                      }
                    }),
                  }}
                  value={digit}
                  onChangeText={(value) => handleOtpChange(value, index)}
                  onKeyPress={({ nativeEvent }) => handleKeyPress(nativeEvent.key, index)}
                  keyboardType="number-pad"
                  maxLength={6}
                  selectTextOnFocus
                  autoComplete="one-time-code"
                />
              ))}
            </View>

            {error ? (
              <Text style={{
                fontSize: responsive.typography.sm,
                color: '#EF4444',
                textAlign: 'center',
                fontWeight: '500',
              }}>
                {error}
              </Text>
            ) : null}
          </YStack>

          {/* Actions */}
          <YStack space="$3">
            <TouchableOpacity
              onPress={handleVerify}
              disabled={isVerifying || otpCode.join('').length !== 6}
              style={{
                backgroundColor: (isVerifying || otpCode.join('').length !== 6) ? '#9CA3AF' : '#3B82F6',
                paddingVertical: responsive.spacing.md,
                borderRadius: 12,
                ...Platform.select({
                  web: {
                    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
                  },
                  default: {
                    shadowColor: '#3B82F6',
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.3,
                    shadowRadius: 8,
                    elevation: 8,
                  }
                }),
              }}
            >
              <XStack alignItems="center" justifyContent="center" space="$2">
                {isVerifying && (
                  <FontAwesome6
                    name="spinner"
                    size={16}
                    color="white"
                    style={{ 
                      transform: [{ rotate: isVerifying ? '360deg' : '0deg' }] 
                    }}
                  />
                )}
                <Text style={{
                  fontSize: responsive.typography.base,
                  fontWeight: '600',
                  color: 'white',
                  textAlign: 'center',
                }}>
                  {isVerifying ? 'Verifying...' : 'Verify Code'}
                </Text>
              </XStack>
            </TouchableOpacity>

            <XStack justifyContent="space-between" alignItems="center">
              <TouchableOpacity
                onPress={handleResendCode}
                disabled={resendCooldown > 0}
                style={{
                  paddingVertical: responsive.spacing.sm,
                  paddingHorizontal: responsive.spacing.md,
                }}
              >
                <Text style={{
                  fontSize: responsive.typography.sm,
                  color: resendCooldown > 0 ? '#9CA3AF' : '#3B82F6',
                  fontWeight: '500',
                }}>
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleCancel}
                style={{
                  paddingVertical: responsive.spacing.sm,
                  paddingHorizontal: responsive.spacing.md,
                }}
              >
                <Text style={{
                  fontSize: responsive.typography.sm,
                  color: theme ? '#6B7280' : '#D1D5DB',
                  fontWeight: '500',
                }}>
                  Cancel
                </Text>
              </TouchableOpacity>
            </XStack>
          </YStack>
          </ScrollView>
        </Animatable.View>
      </View>
    </Modal>
  );
});

OTPModal.displayName = 'OTPModal';

export default OTPModal;
